import * as React from 'react';

import { DrawerContext } from '@notino/react-styleguide';

import { MultistepModal } from '@components/MultistepModal';
import { useIsDesktop } from '@utils/helpers';

import { DRAWER_SHADEFINDER_CONFIG } from '../config';
import { ModalProps, DrawerProps } from '../types';

function transformModalToDrawerState({
  onClose: _onClose, // don't pass to state, so we don't override the drawer default functions
  onBack: _onBack,
  ...modalProps
}: ModalProps): DrawerProps {
  const isEmptyTitle =
    modalProps.title?.({
      currentStep: modalProps.currentStep,
      totalSteps: modalProps.totalSteps,
    }) === '';

  return {
    ...modalProps,
    ...DRAWER_SHADEFINDER_CONFIG,
    showProgress: modalProps.showProgress,
    title: isEmptyTitle ? undefined : DRAWER_SHADEFINDER_CONFIG.title,
  };
}

function drawerStateToModalState({
  onClose: _onClose, // don't pass to state, so we don't override the modal default functions
  onBack: _onBack,
  ...drawerState
}: DrawerProps): ModalProps {
  return {
    totalSteps: drawerState.totalSteps,
    currentStep: drawerState.currentStep,
    showProgress: drawerState.showProgress,
    showBack: false,
    title: drawerState.title ? undefined : () => '',
    modalProps: {
      background: drawerState.background,
    },
  };
}

/**
 * When screen is resized, change to drawer or modal according to viewport
 */
export const useScreenResizeChangeContainer = () => {
  const {
    closeModal,
    getState: getModalState,
    restore: restoreModal,
  } = MultistepModal.useMultistepModal();
  const {
    closeWithoutAnimation: closeDrawer,
    getState: getDrawerState,
    restore: restoreDrawer,
  } = DrawerContext.useDrawerContext();

  const isDesktop = useIsDesktop();

  React.useEffect(() => {
    if (!isDesktop) {
      const modalState = getModalState();

      if (modalState.current.element) {
        closeModal();
        restoreDrawer(
          {
            element: modalState.current.element,
            props: transformModalToDrawerState(modalState.current.props),
          },
          modalState.previous.map(({ element, props }) => ({
            element,
            props: transformModalToDrawerState(props),
          }))
        );
      }
    } else {
      const drawerState = getDrawerState();

      if (drawerState.current.element) {
        closeDrawer();
        restoreModal(
          {
            element: drawerState.current.element,
            props: drawerStateToModalState(drawerState.current.props),
          },
          drawerState.previous.map(({ element, props }) => ({
            element,
            props: drawerStateToModalState(props),
          }))
        );
      }
    }
  }, [
    closeModal,
    closeDrawer,
    getDrawerState,
    getModalState,
    restoreDrawer,
    restoreModal,
    isDesktop,
  ]);
};
